import React, { useCallback, useEffect, useMemo, useState } from "react";
import { notification, Popconfirm, Spin, Tooltip } from "antd";
import classes from "./edit-add-objects.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsShown,
  selectPdfDetectedFields,
} from "../../../../reducers/pdf-detected-fields/selectors";
import { setIsShown } from "../../../../reducers/pdf-detected-fields/actions";
import {
  faEdit,
  faFile,
  faFilePen,
  faImage,
  faRectangleList,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import {
  selectJsonTemplateData,
  selectProjectObject,
  selectTestResultUrl,
  selectTogglePreview,
} from "../../../../reducers/project/selectors";
import { MODALS, OBJECT_TYPES } from "../../../../constants/project";
import {
  selectActiveDetectedFieldId,
  selectActiveObjectId,
  settingObjectDefaultSizes,
} from "../../../../reducers/editor/selectors";
import {
  setActiveDetectedField,
  setActiveObjectId,
} from "../../../../reducers/editor/actions";
import {
  fillPdfForm,
  setDraftingObject,
  setTestResultUrl,
  togglePreviewFunc,
} from "../../../../reducers/project/actions";
import {
  generateRectangleField,
  generateRectangleFromDetectedField,
  generateRectangleFromProjectObject,
} from "../../../../helpers/objectGenerators";
import {
  selectPage,
  selectTotalPages,
  selectUrl,
} from "../../../../reducers/pdf-viewer/selectors";
import { has, isEmpty, isNil, pathOr, sort } from "ramda";
import PreviewModal from "../../../../components/preview-modal/preview-modal";
import { setPdfPage } from "../../../../reducers/pdf-viewer/actions";
import { showErrorMessageFromServer } from "../../../../helpers/Request.helper";
import copy from "copy-to-clipboard";
import { roundUpCoordinate } from "../../../../helpers/common";
import {
  getAnnotationPureData,
  getFieldPureData,
  getImagePureData,
} from "../../../../helpers/objects";

const EditAddObjects = ({ onDelete = () => {} }) => {
  const dispatch = useDispatch();
  const isDetectedFieldsShown = useSelector(selectIsShown);
  const projectObjects = useSelector(selectProjectObject);
  const activeObjectId = useSelector(selectActiveObjectId);
  const activeDetectedFieldId = useSelector(selectActiveDetectedFieldId);
  const testResultUrl = useSelector(selectTestResultUrl);
  const pdfUrl = useSelector(selectUrl);
  const detectedFields = useSelector(selectPdfDetectedFields);
  const currentPage = useSelector(selectPage);
  const togglePreView = useSelector(selectTogglePreview);
  const totalPages = useSelector(selectTotalPages);
  const objectDefaultSizes = useSelector(settingObjectDefaultSizes);
  const jsonTemplateData = useSelector(selectJsonTemplateData);

  const [isTesting, setIsTesting] = useState(false);
  const [modalShow, setModalShow] = useState(null);

  const onChangeShownDetectFields = () => {
    dispatch(setIsShown(!isDetectedFieldsShown));
  };

  const onCopyXY = (item) => {
    if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      copy(`${roundUpCoordinate(item.left)}, ${roundUpCoordinate(item.top)}`);
    } else {
      copy(`${roundUpCoordinate(item.x)}, ${roundUpCoordinate(item.y)}`);
    }
    notification.success({
      message: "Copied!",
    });
  };

  const onCopyRectangle = (item) => {
    if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      copy(
        `${roundUpCoordinate(item.left)}, ${roundUpCoordinate(
          item.top
        )}, ${roundUpCoordinate(item.width)}, ${roundUpCoordinate(item.height)}`
      );
    } else {
      copy(
        `${roundUpCoordinate(item.x)}, ${roundUpCoordinate(
          item.y
        )}, ${roundUpCoordinate(item.width)}, ${roundUpCoordinate(item.height)}`
      );
    }
    notification.success({
      message: "Copied!",
    });
  };

  const onClickItem = (item) => {
    const page = Number(item.pages) + 1;
    if (page !== currentPage && page <= totalPages) {
      dispatch(setPdfPage(page));
    }
    if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      dispatch(setActiveDetectedField(item.objectId ? item.objectId : item.id));
    } else {
      dispatch(setActiveObjectId(item.objectId));
    }
  };

  const onEditItem = (item) => {
    let field;
    if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      field = generateRectangleFromDetectedField(item);
    } else {
      field = generateRectangleFromProjectObject(item);
    }
    dispatch(setDraftingObject(field));
  };

  const shownDetectedFieldsData = useMemo(() => {
    const projectDetectedFieldObjectFieldNames = projectObjects
      .filter((item) => item.objectType === OBJECT_TYPES.DETECTED_FIELD)
      .map((item) => item.objectId);

    return detectedFields.filter(
      (item) => !projectDetectedFieldObjectFieldNames.includes(item.id)
    );
  }, [detectedFields, projectObjects]);

  const getIcon = useCallback(
    (item) => {
      switch (item.objectType) {
        case OBJECT_TYPES.DETECTED_FIELD:
          return <FontAwesomeIcon icon={faRectangleList} />;
        case OBJECT_TYPES.ANNOTATION:
          return <FontAwesomeIcon icon={faFilePen} />;
        case OBJECT_TYPES.IMAGE:
          return (
            <FontAwesomeIcon className={classes.ImageIcon} icon={faImage} />
          );
      }
    },
    [projectObjects]
  );

  const isDetectedField = useCallback((item) => {
    if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      return !has("objectId", item);
    }
  }, []);

  const getName = useCallback(
    (item) => {
      const isDetectedFieldItem = isDetectedField(item);
      switch (item.objectType) {
        case OBJECT_TYPES.DETECTED_FIELD:
          return (
            <div>
              <div>
                {isDetectedFieldItem && isEmpty(item.text)
                  ? " < not set > "
                  : item.text}
              </div>
              <div className={classes.detectedFieldName}>
                {item.altFieldName}
              </div>
            </div>
          );
        case OBJECT_TYPES.ANNOTATION:
          return isEmpty(item.text) ? " < not set > " : item.text;
        case OBJECT_TYPES.IMAGE:
          return item.url;
      }
    },
    [projectObjects]
  );

  const onTest = () => {
    setIsTesting(true);
    try {
      const data = {
        fields: projectObjects
          .filter((item) => item.objectType === OBJECT_TYPES.DETECTED_FIELD)
          .map((item) => {
            return getFieldPureData(item);
          }),
        annotations: projectObjects
          .filter((item) => item.objectType === OBJECT_TYPES.ANNOTATION)
          .map((item) => {
            return getAnnotationPureData(item);
          }),
        images: projectObjects
          .filter((item) => item.objectType === OBJECT_TYPES.IMAGE)
          .map((item) => {
            return getImagePureData(item);
          }),
        templateData: jsonTemplateData ? jsonTemplateData : `{}`,
      };

      // check if nothing to add actually
      if (
        isEmpty(data.fields) &&
        isEmpty(data.annotations) &&
        isEmpty(data.images)
      ) {
        setIsTesting(false);
        const erorrMsg =
          "Please add at least one text, image object or change a pdf field and try again";
        notification.warning({
          message: erorrMsg,
        });
        // reset to the initial state
        setIsTesting(false);
        dispatch(togglePreviewFunc(false));

        return Promise.reject(erorrMsg);
      } else {
        // sending request to pdf/edit/add
        dispatch(fillPdfForm({ url: pdfUrl, data: data }))
          .unwrap()
          .then((res) => {
            const url = pathOr(null, ["url"], res);
            dispatch(setTestResultUrl(url));
            setIsTesting(false);
          })
          .catch(showErrorMessageFromServer)
          .finally(() => {
            setIsTesting(false);
            dispatch(togglePreviewFunc(false));
          });
      }
    } finally {
      // finalization code
    }
  };

  const onOpenResultUrl = () => {
    setModalShow(MODALS.testResultUrlModal);
  };

  const sortedProjectObjects = useMemo(() => {
    return sort((a, b) => {
      return a.pages - b.pages;
    }, projectObjects);
  }, [projectObjects]);

  const onAddObjectByButton = (objectType) => {
    const rectangleObject = generateRectangleField([], {
      topLeftCornerX: 2,
      topLeftCornerY: 2,
      width: objectDefaultSizes.width,
      height: objectDefaultSizes.height,
      page: currentPage - 1,
    });

    dispatch(
      setDraftingObject({
        ...rectangleObject,
        data: { objectType: objectType },
      })
    );
    dispatch(setActiveObjectId(rectangleObject.id));
  };

  const isActive = useCallback(
    (item) => {
      if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
        return item.objectId
          ? activeDetectedFieldId === item.objectId
          : activeDetectedFieldId === item.id;
      }
      return activeObjectId === item.objectId;
    },
    [activeObjectId, activeDetectedFieldId]
  );

  const shownData = useMemo(() => {
    return [...sortedProjectObjects, ...shownDetectedFieldsData];
  }, [shownDetectedFieldsData, sortedProjectObjects]);

  const isShowCopyRectangle = useCallback((item) => {
    return item.width || item.height;
  }, []);

  useEffect(() => {
    if (!isNil(testResultUrl)) {
      onOpenResultUrl();
    }
  }, [testResultUrl]);

  useEffect(() => {
    if (togglePreView) {
      onTest();
    }
  }, [togglePreView]);

  return (
    <div className={classes.EditAddObjectsWrap}>
      <div className={classes.ListWrap}>
        {!shownData.length && <div className={classes.NoData}>No Data</div>}

        {shownData.map((item) => (
          <Tooltip
            key={has("objectId", item) ? item.objectId : item.id}
            title="double-click to show properties"
          >
            <div
              key={has("objectId", item) ? item.objectId : item.id}
              className={classNames(classes.ProjectObjectItemWrap, {
                [classes.ActiveObject]: isActive(item),
              })}
              onClick={() => onClickItem(item)}
              onDoubleClick={() => onEditItem(item)}
            >
              <div className={classes.FirstWrap}>
                <div className="me-1">
                  <FontAwesomeIcon
                    className={classNames(classes.EditIcon, "text-primary")}
                    icon={faEdit}
                    onClick={() => onEditItem(item)}
                  />
                </div>
                <div className={"text-secondary"}>
                  {getIcon(item)}
                  <div className={classes.PageWrap}>
                    <FontAwesomeIcon
                      icon={faFile}
                      className={classes.PageIcon}
                    />
                    <span
                      className={classNames(classes.PageNum, "text-primary")}
                    >
                      {Number(item.pages) + 1}
                    </span>
                  </div>
                </div>
              </div>

              <div className={classes.SecondWrap}>
                <div className={classes.ItemInfoWrap}>
                  <div className={classes.ItemInfo}>
                    <div className={classes.ProjectObjectName}>
                      {getName(item)}
                    </div>
                  </div>
                  <div className={classes.ActionWrap}>
                    {!isDetectedField(item) && (
                      <Popconfirm
                        placement="top"
                        title="Are you sure you want to delete this object?"
                        onConfirm={() => onDelete(item.objectId)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          className={classNames(
                            classes.DeleteIcon,
                            "text-danger",
                            {
                              [classes.Disabled]: isDetectedField(item),
                            }
                          )}
                        />
                      </Popconfirm>
                    )}
                  </div>
                </div>
                <div className={classes.CopyWrap}>
                  <span
                    className={classNames(classes.Copy, "text-primary")}
                    onClick={() => onCopyXY(item)}
                  >
                    copy x,y
                  </span>

                  {isShowCopyRectangle(item) && (
                    <span
                      className={classNames(classes.Copy, "text-primary ms-3")}
                      onClick={() => onCopyRectangle(item)}
                    >
                      copy rectangle
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Tooltip>
        ))}
      </div>

      {/*<div className={classes.TestActionWrap}>*/}
      {/*  <div className={classes.ShowDetectedFieldButton}>*/}
      {/*    <Switch*/}
      {/*      defaultChecked={isDetectedFieldsShown}*/}
      {/*      onChange={onChangeShownDetectFields}*/}
      {/*    />*/}
      {/*    <span>highlight interactive fields</span>*/}
      {/*  </div>*/}
      {/*</div>*/}

      <PreviewModal
        isOpen={modalShow === MODALS.testResultUrlModal}
        handleClose={() => setModalShow(null)}
        fileName={testResultUrl}
        uri={testResultUrl}
      />
    </div>
  );
};

export default EditAddObjects;
